import React from "react";
import styled, { css } from "styled-components";
import { useEvent } from "../hooks/useEvent";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { useIsFocused } from "../hooks/useIsFocused";
import { Icon } from "../Icon";
import { PropError, PropLabel } from "../PropX";
import { style } from "../style";
import { hexWithOpacityToRBGA } from "../utils/hexWithOpacityToRBGA";
import { LooseInteractionButton, LooseTextInput } from "../__Internal__";
import { calculateRealMax } from "./calculateRealMax";
import { useStepperEventHandlers } from "./useStepperEventHandlers";

type Props = {
  /**
   * The name of the stepper.
   */
  name: string;

  /**
   * The current value of the stepper.
   */
  value: number;

  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * A label to display above the select menu.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * The unit to display next to the value when the input is not active.
   * Receives the current value as an argument so that it can be formatted.
   * @default (x) => `${x}`
   */
  unit?: (value: number) => string;

  /**
   * The minimum value of the stepper.
   * @default 0
   */
  min?: number;

  /**
   * The maximum value of the stepper.
   * @default Infinity
   */
  max?: number;

  /**
   * The step size of the stepper.
   * @default 1
   */
  step?: number;

  /**
   * Whether the stepper is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the stepper is required.
   * @default false
   */
  required?: boolean;

  /**
   * An error to attached to the input.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "blur"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * Testing utility tag.
   * @default "full-stepper"
   */
  "data-testid"?: string;
};

type TextOverlayProps = {
  $invalid: boolean;
};

const TextOverlay = styled.button<TextOverlayProps>`
  all: unset;

  display: flex;

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  align-items: center;
  justify-content: center;
  text-align: center;

  &&&& {
    height: 48px;
    border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.primary};
    box-sizing: border-box;
    border-radius: 0;
    padding: 0 ${style.vars.whitespace.regular};

    background: white;
    cursor: text;

    :focus:not(:disabled) {
      outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
      outline-offset: ${style.vars.borderWidth.thin};
      z-index: ${style.vars.zIndex.raised};
    }

    :disabled {
      background: white;
      border-color: ${hexWithOpacityToRBGA(style.colors.grey.primary, 0.3)};
      color: ${hexWithOpacityToRBGA(style.colors.grey.darkest, 0.3)};
      cursor: unset;
      outline: none;
    }

    ${(props) => {
      if (!props.disabled && props.$invalid) {
        return css`
          border-color: ${style.colors.red.dark};

          :disabled {
            border-color: ${hexWithOpacityToRBGA(style.colors.red.dark, 0.3)};
          }
        `;
      }
    }}
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

const OuterContainer = styled.div`
  display: inline-block;
  min-width: calc(200px + 2 * ${style.vars.whitespace.regular});
`;

const InnerContainer = styled.div`
  all: unset;
  display: flex;
  flex: 0 1 0;
  min-width: calc(200px + 2 * ${style.vars.whitespace.regular});
`;

const ButtonWrapper = styled.div`
  height: 48px;
  width: 48px;
  position: relative;
`;

const DisplayWrapper = styled.div`
  flex-grow: 1;
`;

const noopUnit = (value: number) => `${value}`;

export const FullStepper: React.FC<Props> = (props) => {
  const { name, value, label, disabled = false, required = false, unit = noopUnit, validateOn = "blur", step = 1 } = props;
  const testId = props["data-testid"] ?? "full-stepper";
  const ref = React.useRef<HTMLInputElement>(null);

  /**
   * `min` must be greater or equal to zero.
   * `max` must be greater or equal to `min`.
   *
   * If `min` is less than zero, it will be set to zero.
   * If `max` is less than `min`, it will be set to `min`.
   */
  const min = Math.max(0, props.min ?? 0);
  let max = Math.max(min, Math.min(Infinity, props.max ?? Infinity));
  max = calculateRealMax(min, max, step);

  const handlers = useStepperEventHandlers({ value, min, max, ref, step });

  const isMin = value <= min;
  const isMax = value >= max;

  const [errors, propsForInput] = useHTMLInputElementValidationProjection(props, { ref, validateOn });
  const { invalid } = propsForInput;

  const inputIsFocused = useIsFocused(ref);

  const onClickText = useEvent(() => {
    if (disabled) {
      return;
    }

    ref.current?.focus();
  });

  let textOverlay: React.ReactNode = null;

  if (!inputIsFocused) {
    textOverlay = (
      <TextOverlay data-testid={`${testId}/overlay`} disabled={disabled} $invalid={invalid} onClick={onClickText}>
        {unit(value)}
      </TextOverlay>
    );
  }

  return (
    <OuterContainer>
      {label && PropLabel.renderForBeforeInput(label, { invalid, required })}
      <InnerContainer>
        <ButtonWrapper>
          <LooseInteractionButton
            data-testid={`${testId}/decrement`}
            onClick={handlers.onClickDecrementButton}
            onMouseDown={handlers.onMouseDownDecrementButton}
            onMouseUp={handlers.onStop}
            onMouseLeave={handlers.onStop}
            disabled={disabled || isMin}
            invalid={invalid}
            borderRight={false}
            borderRadiusTopLeft="base"
            borderRadiusBottomLeft="base"
          >
            <Icon icon="minus" />
          </LooseInteractionButton>
        </ButtonWrapper>
        <DisplayWrapper>
          <InputWrapper>
            <LooseTextInput
              data-testid={testId}
              type="number"
              name={name}
              id={name}
              ref={ref}
              min={min}
              max={max}
              step={step}
              value={value}
              required={required}
              disabled={disabled}
              aria-hidden={!inputIsFocused}
              UNSAFE_borderRadiusBottomLeft="none"
              UNSAFE_borderRadiusBottomRight="none"
              UNSAFE_borderRadiusTopLeft="none"
              UNSAFE_borderRadiusTopRight="none"
              UNSAFE_center
              {...propsForInput}
            />
            {textOverlay}
          </InputWrapper>
        </DisplayWrapper>
        <ButtonWrapper>
          <LooseInteractionButton
            data-testid={`${testId}/increment`}
            onClick={handlers.onClickIncrementButton}
            onMouseDown={handlers.onMouseDownIncrementButton}
            onMouseUp={handlers.onStop}
            onMouseLeave={handlers.onStop}
            disabled={disabled || isMax}
            invalid={invalid}
            borderLeft={false}
            borderRadiusTopRight="base"
            borderRadiusBottomRight="base"
          >
            <Icon icon="plus" />
          </LooseInteractionButton>
        </ButtonWrapper>
      </InnerContainer>
      {PropError.renderGroupAfterInput(errors)}
    </OuterContainer>
  );
};
